import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { ApplicationBoard, ApplicationReworkInput, ApplicationStatus } from "@/api"
import { api } from "@/api"

/**
 * Стадии, с которых заявку можно вернуть на доработку (совпадает с проверкой
 * бэкенда): на проверке, уже принятую и повторную доработку.
 */
export const REWORKABLE_STATUSES: readonly ApplicationStatus[] = ["moderation", "rework", "active"]

export const applicationKeys = {
  board: ["applications", "board"] as const,
}

export function useApplicationBoard() {
  return useQuery({
    queryKey: applicationKeys.board,
    queryFn: () => api.applications.board(),
    // Заявки с лендинга приходят в реальном времени, а менеджер держит доску
    // открытой — поэтому подтягиваем свежие каждые 30 с (и при возврате во
    // вкладку), чтобы новая заявка появлялась сама, без перезагрузки. Ручное
    // обновление на странице — на случай «прямо сейчас».
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

/**
 * Move a card between columns. Optimistic: drag-and-drop must feel instant, so
 * the board is patched locally first and rolled back if the call fails.
 */
export function useMoveApplication() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: ApplicationStatus; reason?: string }) =>
      api.applications.move(id, status, reason),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: applicationKeys.board })
      const previous = queryClient.getQueryData<ApplicationBoard>(applicationKeys.board)
      queryClient.setQueryData<ApplicationBoard>(applicationKeys.board, (board) =>
        board
          ? {
              ...board,
              applications: board.applications.map((entry) =>
                entry.id === id
                  ? { ...entry, status, statusChangedAt: new Date().toISOString() }
                  : entry,
              ),
            }
          : board,
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(applicationKeys.board, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: applicationKeys.board }),
  })
}

export function useAssignApplication() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, assigneeId }: { id: string; assigneeId: string | null }) =>
      api.applications.assign(id, assigneeId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: applicationKeys.board }),
  })
}

/**
 * Send an application to «На доработке» with a written remark. Invalidates the
 * board so the card lands in the rework column with a fresh «в статусе» counter.
 */
export function useReworkApplication() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ApplicationReworkInput }) =>
      api.applications.rework(id, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: applicationKeys.board })
      // П7: доработка документа меняет его статус, раздел «Документы» и значок.
      void queryClient.invalidateQueries({ queryKey: ["sellers"] })
    },
  })
}
